/**
 * A edição que a home mostra em detalhe.
 *
 * Três seções do template — o placar em destaque, as abas de próximas e
 * passadas, e os cards de equipe — falam sobre *uma* edição, não sobre a
 * competição inteira. Este gancho decide qual é essa edição e carrega o que as
 * três precisam, uma vez só.
 *
 * A escolha segue a ordem em que alguém procuraria:
 *
 *   1. a que está sendo disputada agora (grupos ou mata-mata) — é a que tem
 *      partida acontecendo, e placar em movimento é o que traz gente de volta;
 *   2. na falta dela, a que está com inscrições abertas;
 *   3. e por último a última encerrada, para a página não ficar muda entre uma
 *      temporada e outra.
 *
 * As consultas só disparam quando existe uma edição escolhida (`enabled`). Sem
 * isso, o React Query chamaria `/tournaments/undefined/matches` no primeiro
 * render de todo visitante, e o log da API ficaria cheio de 404 previsíveis.
 */

import { useQuery } from '@tanstack/react-query';
import { queries, type MatchDto, type StandingRowDto, type TeamDto, type TournamentSummary } from '@/lib/api';

/** Estados em que a edição tem partida para mostrar. */
const EM_ANDAMENTO = ['group_stage', 'knockout'];

export interface EdicaoEmDestaque {
  edicao: TournamentSummary | null;
  partidas: MatchDto[];
  /** Times da edição, indexados por id — vem junto da classificação. */
  times: Record<string, TeamDto>;
  /** Uma linha de classificação por time, achatada dos grupos. */
  classificacao: Record<string, StandingRowDto>;
  carregando: boolean;
}

export function useEdicaoEmDestaque(): EdicaoEmDestaque {
  const { data: dados, isLoading: carregandoEdicoes } = useQuery(queries.tournaments());
  const edicoes = dados?.tournaments ?? [];

  const edicao =
    edicoes.find((e) => EM_ANDAMENTO.includes(e.status)) ??
    edicoes.find((e) => e.status === 'registration') ??
    edicoes
      .filter((e) => e.status === 'finished')
      .sort((a, b) => (b.startsAt ?? 0) - (a.startsAt ?? 0))[0] ??
    null;

  const slug = edicao?.slug;

  const { data: dadosPartidas, isLoading: carregandoPartidas } = useQuery({
    ...queries.matches(slug ?? ''),
    enabled: Boolean(slug),
  });

  const { data: dadosTabela, isLoading: carregandoTabela } = useQuery({
    ...queries.standings(slug ?? ''),
    enabled: Boolean(slug),
  });

  /*
   * A classificação chega agrupada por grupo (`{ A: [...], B: [...] }`). Aqui
   * ela vira um índice por time: quem consome quer "quantos pontos o time X
   * tem", e não "em que grupo ele está".
   */
  const classificacao: Record<string, StandingRowDto> = {};
  for (const linhas of Object.values(dadosTabela?.standings ?? {})) {
    for (const linha of linhas) classificacao[linha.teamId] = linha;
  }

  return {
    edicao,
    partidas: (dadosPartidas?.matches ?? []).map((m) => m.match),
    times: dadosTabela?.teams ?? {},
    classificacao,
    carregando: carregandoEdicoes || carregandoPartidas || carregandoTabela,
  };
}

/** As partidas que ainda não têm placar, da mais próxima para a mais distante. */
export function proximas(partidas: MatchDto[]): MatchDto[] {
  return partidas
    .filter((p) => p.homeGoals === null && p.awayGoals === null)
    .sort((a, b) => (a.scheduledAt ?? Infinity) - (b.scheduledAt ?? Infinity));
}

/** As já decididas, da mais recente para a mais antiga. */
export function passadas(partidas: MatchDto[]): MatchDto[] {
  return partidas
    .filter((p) => p.homeGoals !== null && p.awayGoals !== null)
    .sort((a, b) => (b.scheduledAt ?? 0) - (a.scheduledAt ?? 0));
}

/** Peso de decisão da fase — quanto maior, mais a partida vale. */
const PESO: Record<string, number> = {
  final: 5,
  semi: 4,
  third_place: 3,
  quarter: 2,
  group: 1,
};

/**
 * A partida que a home coloca no holofote.
 *
 * Não é simplesmente a próxima do calendário, e a diferença aparece no dia em
 * que mais importa: a disputa de 3º lugar costuma ser marcada para antes da
 * final, então "a próxima" seria ela — e a home anunciaria o jogo menor no dia
 * em que a competição decide o campeão.
 *
 * A regra é: encontra o dia da próxima partida e, dentro daquele dia, destaca a
 * mais decisiva. Continua sendo uma partida que está para acontecer — só não é
 * necessariamente a primeira do relógio.
 *
 * Sem nada pendente, cai para a última disputada: numa edição encerrada, o
 * confronto que interessa é a final que acabou de acontecer.
 */
export function partidaEmDestaque(partidas: MatchDto[]): MatchDto | null {
  const pendentes = proximas(partidas);
  if (pendentes.length === 0) return passadas(partidas)[0] ?? null;

  const primeira = pendentes[0]!;
  if (primeira.scheduledAt === null) return primeira;

  /* O dia da próxima partida, em UTC — a janela dentro da qual vale escolher
     pela importância em vez de pelo relógio. */
  const DIA = 86400;
  const diaDaProxima = Math.floor(primeira.scheduledAt / DIA);

  const noMesmoDia = pendentes.filter(
    (p) => p.scheduledAt !== null && Math.floor(p.scheduledAt / DIA) === diaDaProxima,
  );

  return noMesmoDia.reduce((melhor, atual) =>
    (PESO[atual.stage] ?? 0) > (PESO[melhor.stage] ?? 0) ? atual : melhor,
  );
}
